/**
 * Stock reference API (M2, W-31): units, warehouses / zones / locations, work
 * centres and the `settings.stock` family. Quantities never move through here —
 * the posting engine (`fn_post_stock_movement`) belongs to the movement spec.
 *
 * Names are deliberately distinct from `api/platform.ts`, which already exports
 * `listUnits`, `updateUnit`, `listWarehouses` and `saveWarehouseSetup` for the
 * setup wizard: both files are re-exported from the same `@aslen/core/api`
 * barrel, so a collision would be silent.
 */
import type { AslenClient, Json, Tables, TablesInsert, TablesUpdate, Views } from '../db';
import type { LocationType, StockMovementKind } from '../constants';
import { toDb } from '../money';
import { parseCountReview, type CountKind, type CountReview } from '../stock/counts';
import { parseCountConflicts, parseCountSession, type CountConflictRow, type CountSession } from '../stock/session';
import { groupLocationStock, lastCountsByLocation, type LastCounts, type WarehouseStock } from '../stock/locations';
import { movementPayload, type PostMovementInput, type PostedMovement } from '../stock/movement';
import { parseScanResolution, summariseLocationStock, type ScanLocationFacts, type ScanResolution } from '../stock/scan';
import { parseLotTrace, parseTraceNodeDetail, type LotTrace, type TraceDirection, type TraceNodeDetail } from '../stock/trace';
import { unwrap } from './errors';

export type Unit = Tables<'units'>;
export type UnitUsageRow = Views<'v_unit_usage'>;
export type Warehouse = Tables<'warehouses'>;
export type WarehouseRow = Views<'v_warehouses'>;
export type WarehouseZone = Tables<'warehouse_zones'>;
export type StockLocation = Tables<'locations'>;
export type WorkCenter = Tables<'work_centers'>;
export type WorkCenterRow = Views<'v_work_center_summary'>;
export type WorkCenterRate = Tables<'work_center_rates'>;
export type StockKpis = Views<'v_stock_kpis'>;

// ── units (W-31 › Birimler) ─────────────────────────────────────────────────
/**
 * How often each unit is referenced. The toggle in "Ölçü birimleri" is disabled
 * from these counts, and the SQL guard refuses the write anyway — the view is
 * only there so the reason can be shown before the click.
 */
export async function listUnitUsage(client: AslenClient, companyId: string): Promise<UnitUsageRow[]> {
  return unwrap(await client.from('v_unit_usage').select('*').eq('company_id', companyId).order('unit_code'));
}

/**
 * Adds or edits a unit. The nine seeded units are `is_system`: their code cannot
 * change and they cannot be deleted, but they may be renamed — the trigger
 * enforces that, this is a plain upsert.
 */
export async function upsertUnit(client: AslenClient, row: TablesInsert<'units'>): Promise<Unit> {
  return unwrap(await client.from('units').upsert(row).select().single());
}

/** Units are hard-deleted (never referenced) or deactivated; there is no `deleted_at`. */
export async function deleteUnit(client: AslenClient, id: string) {
  unwrap(await client.from('units').delete().eq('id', id).eq('is_system', false).select('id'));
}

// ── warehouses, zones, locations (W-31 › Depolar / lokasyonlar) ──────────────
export interface WarehouseTree {
  warehouses: WarehouseRow[];
  zones: WarehouseZone[];
  locations: StockLocation[];
}

/**
 * Everything the Depolar section draws, in three round trips instead of a
 * nested select: the list needs counts per warehouse (from `v_warehouses`), the
 * detail view needs the zones as chips and the locations as a table, and the
 * stock-matrix card needs the zones of every warehouse at once. The hidden
 * default location (`is_default`) is included — callers that show a user-facing
 * list filter it out with `userLocations()` from `api/platform`.
 */
export async function listWarehouseTree(client: AslenClient, companyId: string): Promise<WarehouseTree> {
  const [warehouses, zones, locations] = await Promise.all([
    client.from('v_warehouses').select('*').eq('company_id', companyId).is('deleted_at', null).order('type').order('sort'),
    client.from('warehouse_zones').select('*').eq('company_id', companyId).is('deleted_at', null).order('sort').order('code'),
    client.from('locations').select('*').eq('company_id', companyId).is('deleted_at', null).order('sort').order('code'),
  ]);
  return { warehouses: unwrap(warehouses), zones: unwrap(zones), locations: unwrap(locations) };
}

/**
 * The three facts the warehouse detail view draws that the tree does not carry:
 * what sits in each location (the "Kalem" column and the "negatif" chip), which
 * count last touched it, and whether the warehouse ever saw a movement.
 *
 * The last one is what `private.warehouse_has_movements` checks before it
 * refuses a code or type change; the client asks so the drawer can hide the
 * action instead of offering a write that the trigger will throw back.
 */
export interface WarehouseFacts {
  stock: WarehouseStock;
  counts: LastCounts;
  hasMovements: boolean;
}

export async function getWarehouseFacts(client: AslenClient, companyId: string, warehouseId: string): Promise<WarehouseFacts> {
  const [balances, counts, movements] = await Promise.all([
    client
      .from('v_stock_by_location')
      .select('location_id, item_id, qty_base, qty_stock_unit, stock_unit, lot_count')
      .eq('company_id', companyId)
      .eq('warehouse_id', warehouseId),
    client
      .from('v_stock_counts')
      .select('count_no, counted_at, location_codes')
      .eq('company_id', companyId)
      .eq('warehouse_id', warehouseId)
      .not('counted_at', 'is', null)
      .order('counted_at', { ascending: false })
      .limit(200),
    client
      .from('stock_movements')
      .select('id')
      .eq('company_id', companyId)
      .or(`from_warehouse_id.eq.${warehouseId},to_warehouse_id.eq.${warehouseId}`)
      .limit(1),
  ]);

  return {
    stock: groupLocationStock(unwrap(balances)),
    counts: lastCountsByLocation(unwrap(counts).map((r) => ({ count_no: r.count_no, at: r.counted_at, location_codes: r.location_codes }))),
    hasMovements: unwrap(movements).length > 0,
  };
}

export async function upsertWarehouse(client: AslenClient, row: TablesInsert<'warehouses'>): Promise<Warehouse> {
  return unwrap(await client.from('warehouses').upsert(row).select().single());
}

export async function updateWarehouse(client: AslenClient, id: string, patch: TablesUpdate<'warehouses'>): Promise<Warehouse> {
  return unwrap(await client.from('warehouses').update(patch).eq('id', id).select().single());
}

export async function upsertZone(client: AslenClient, row: TablesInsert<'warehouse_zones'>): Promise<WarehouseZone> {
  return unwrap(await client.from('warehouse_zones').upsert(row).select().single());
}

export async function archiveZone(client: AslenClient, id: string) {
  unwrap(await client.from('warehouse_zones').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

export async function upsertLocation(client: AslenClient, row: TablesInsert<'locations'>): Promise<StockLocation> {
  return unwrap(await client.from('locations').upsert(row).select().single());
}

export async function updateLocation(client: AslenClient, id: string, patch: TablesUpdate<'locations'>): Promise<StockLocation> {
  return unwrap(await client.from('locations').update(patch).eq('id', id).select().single());
}

export async function archiveLocation(client: AslenClient, id: string) {
  unwrap(await client.from('locations').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('is_default', false).select('id'));
}

export interface BulkLocationsInput {
  warehouseId: string;
  zoneId?: string | null;
  start?: number;
  end?: number;
  /** Digits in the running number: 2 → "01", 3 → "001". */
  pad?: number;
  type?: LocationType;
}

/**
 * "Toplu lokasyon ekle". Atomic, capped at 200 per call, and existing codes are
 * skipped — the returned number is how many rows were actually created, which
 * is what the toast counts.
 */
export async function createLocationsBulk(client: AslenClient, input: BulkLocationsInput): Promise<number> {
  return unwrap(
    await client.rpc('create_locations_bulk', {
      p_warehouse: input.warehouseId,
      p_zone: input.zoneId ?? undefined,
      p_start: input.start,
      p_end: input.end,
      p_pad: input.pad,
      p_type: input.type,
    }),
  ) as number;
}

// ── work centres (W-31 › İş merkezleri) ─────────────────────────────────────
/**
 * The list reads the summary view, never the table: the cost columns are
 * masked there for operator and guest, and the machine counts and BOM usage are
 * already joined.
 */
export async function listWorkCenters(client: AslenClient, companyId: string): Promise<WorkCenterRow[]> {
  return unwrap(await client.from('v_work_center_summary').select('*').eq('company_id', companyId).is('deleted_at', null).order('sort').order('code'));
}

export async function upsertWorkCenter(client: AslenClient, row: TablesInsert<'work_centers'>): Promise<WorkCenter> {
  return unwrap(await client.from('work_centers').upsert(row).select().single());
}

export async function archiveWorkCenter(client: AslenClient, id: string) {
  unwrap(await client.from('work_centers').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

export interface WorkCenterRateInput {
  /** Base currency (₺) per hour. **[[PLACEHOLDER]]** until the owner enters it. */
  hourlyCostBase?: string | null;
  labourRatePerHourBase?: string | null;
  validFrom?: string | null;
  note?: string | null;
}

/**
 * The cost sidecar, kept apart from `work_centers` so RLS can hide it from
 * operator and guest without revoking columns (decision R1).
 */
export async function saveWorkCenterRates(
  client: AslenClient,
  companyId: string,
  workCenterId: string,
  rates: WorkCenterRateInput,
): Promise<WorkCenterRate> {
  return unwrap(
    await client
      .from('work_center_rates')
      .upsert(
        {
          company_id: companyId,
          work_center_id: workCenterId,
          hourly_cost_base: rates.hourlyCostBase ? Number(toDb(rates.hourlyCostBase)) : null,
          labour_rate_per_hour_base: rates.labourRatePerHourBase ? Number(toDb(rates.labourRatePerHourBase)) : null,
          valid_from: rates.validFrom ?? null,
          note: rates.note ?? null,
        },
        { onConflict: 'work_center_id' },
      )
      .select()
      .single(),
  );
}

// ── settings (W-31b › Stok ve sayım) ────────────────────────────────────────
/** The settings keys this section owns. `mobile` and `item_defaults` sit on the same page. */
export const STOCK_SETTING_KEYS = ['stock', 'costing', 'item_defaults', 'labels', 'mobile', 'stock_matrix', 'imports', 'packaging'] as const;
export type StockSettingKey = (typeof STOCK_SETTING_KEYS)[number];

export type SettingValue = Record<string, Json | undefined>;
export type StockSettings = Record<StockSettingKey, SettingValue>;

const EMPTY: SettingValue = {};

/**
 * Reads the eight keys the stock and settings screens need in one query.
 * A key that has never been written comes back as `{}` rather than undefined, so
 * a screen can read `settings.stock.critical_ratio` without guarding twice.
 */
export async function getStockSettings(client: AslenClient, companyId: string): Promise<StockSettings> {
  const rows = unwrap(await client.from('settings').select('key, value').eq('company_id', companyId).in('key', [...STOCK_SETTING_KEYS]));
  const byKey = new Map(rows.map((r) => [r.key, (r.value ?? {}) as SettingValue]));
  return Object.fromEntries(STOCK_SETTING_KEYS.map((k) => [k, byKey.get(k) ?? EMPTY])) as StockSettings;
}

/**
 * Merges a patch into one settings key. A card edits two or three of a key's
 * values, and the rest of the object — including the `placeholder` array that
 * marks what is still unconfirmed — has to survive, so the current row is read
 * first. `null` in the patch clears a value (the way "Genel gider oranı" is
 * emptied again).
 *
 * Read-then-write, so two partners saving the same key within the same second
 * would leave the later write standing. A card saves its whole key at once and
 * there are two users, so a lock would cost more than it saves.
 */
export async function patchStockSetting(client: AslenClient, companyId: string, key: StockSettingKey, patch: SettingValue): Promise<SettingValue> {
  const rows = unwrap(await client.from('settings').select('value').eq('company_id', companyId).eq('key', key).limit(1));
  const current = (rows[0]?.value ?? {}) as SettingValue;
  const value = { ...current, ...patch } as Json;
  unwrap(await client.from('settings').upsert({ company_id: companyId, key, value }, { onConflict: 'company_id,key' }).select('key'));
  return value as SettingValue;
}

/**
 * Which values of a settings key are still `[[PLACEHOLDER]]` — the seed writes
 * the list into the object itself, and the screen badges those rows.
 */
export function placeholderKeys(value: SettingValue | null | undefined): string[] {
  const raw = value?.placeholder;
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
}

export function isPlaceholder(value: SettingValue | null | undefined, key: string): boolean {
  return placeholderKeys(value).includes(key);
}

/**
 * The patch that both sets a value and takes it off the placeholder list — what
 * a card sends once the owner finally types the confirmed number in.
 */
export function confirmPlaceholder(current: SettingValue | null | undefined, key: string, value: Json): SettingValue {
  return { [key]: value, placeholder: placeholderKeys(current).filter((k) => k !== key) };
}

/**
 * Closes opening stock (E12): after this, no movement may carry the `opening`
 * reason and the opening import is shut. Owner only — the RPC-free write is
 * guarded by RLS plus the settings trigger.
 *
 * `opening_locked_by` keeps the member id so the chip can read
 * "kilitli · 22 Eyl 2026 · Mert" *(W-31b roles board)*; the audit log stays the
 * record of truth.
 */
export async function lockOpeningStock(
  client: AslenClient,
  companyId: string,
  at: string = new Date().toISOString(),
  by?: string | null,
) {
  return patchStockSetting(client, companyId, 'stock', { opening_locked_at: at, opening_locked_by: by ?? null });
}

/**
 * The numbers the "Açılış stoğu kilitlensin mi?" dialog re-writes before the
 * owner confirms: which opening count sessions exist and how many movements
 * carry the `opening` reason. The value being locked comes from
 * `v_stock_kpis.stock_value_base`, which the card already reads.
 */
export interface OpeningStockSummary {
  countNos: string[];
  movements: number;
}

export async function getOpeningStockSummary(client: AslenClient, companyId: string): Promise<OpeningStockSummary> {
  const counts = client
    .from('stock_counts')
    .select('count_no')
    .eq('company_id', companyId)
    .eq('kind', 'opening')
    .is('cancelled_at', null)
    .order('count_no')
    .limit(50);
  const movements = client
    .from('stock_movements')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId)
    .eq('reason', 'opening');
  const [countRows, movementRes] = await Promise.all([counts, movements]);
  unwrap(movementRes);
  return { countNos: unwrap(countRows).map((r) => r.count_no), movements: movementRes.count ?? 0 };
}

// ── on-hand matrix (W-06) ───────────────────────────────────────────────────
export type StockItemSummaryRow = Views<'v_stock_item_summary'>;
export type StockMatrixCellRow = Views<'v_stock_matrix'>;

/**
 * The rows of W-06 (and the figures of W-03 / M-01): one per item with its
 * totals, its status and — for cost roles only — its stock value. The whole
 * company comes in one query: with ≤ 2.000 items on the free tier the filters,
 * facets and the footer sum are cheaper in the browser than in nine round trips,
 * and W-04 already reads `v_items_list` the same way.
 *
 * Inactive items are left out here, as `v_stock_alerts` does: a retired SKU with
 * an old balance would sit in the matrix for ever. Everything else keeps its row,
 * including an item at zero — that is the row "Kritik izleme" exists for.
 */
export async function listStockItemSummary(client: AslenClient, companyId: string): Promise<StockItemSummaryRow[]> {
  const rows = unwrap(await client.from('v_stock_item_summary').select('*').eq('company_id', companyId).order('name_tr'));
  return rows.filter((r) => r.status !== 'inactive');
}

/**
 * The cells: one row per item × zone that has at least one balance row, qty 0
 * included. The zone set (and so the column set) comes from `warehouse_zones`,
 * because a zone whose shelves are all empty still owns its column.
 */
export async function listStockMatrix(client: AslenClient, companyId: string): Promise<StockMatrixCellRow[]> {
  return unwrap(await client.from('v_stock_matrix').select('*').eq('company_id', companyId));
}

// ── read-only helpers the settings and stock screens share ──────────────────
/** Bugün tiles and the W-31b summary box. One row per company. */
export async function getStockKpis(client: AslenClient, companyId: string): Promise<StockKpis | null> {
  const rows = unwrap(await client.from('v_stock_kpis').select('*').eq('company_id', companyId).limit(1));
  return rows[0] ?? null;
}

/** What the import wizard checks before it lets a file through. */
export async function getImportPrerequisites(client: AslenClient, companyId: string) {
  const rows = unwrap(await client.from('v_import_prerequisites').select('*').eq('company_id', companyId).limit(1));
  return rows[0] ?? null;
}

/**
 * Server-side unit conversion for a single item — the mobile keypad's fallback
 * when the UoM rows are not in the cache. With the rows at hand prefer the pure
 * `convertItemQty` from `@aslen/core`, which gives the same answer without a
 * round trip.
 */
export async function convertItemQtyRpc(
  client: AslenClient,
  itemId: string,
  qty: string,
  fromUnit: string,
  toUnit: string,
): Promise<number> {
  return unwrap(
    await client.rpc('convert_item_qty', { p_item_id: itemId, p_qty: Number(toDb(qty, 6)), p_from_unit: fromUnit, p_to_unit: toUnit }),
  ) as number;
}

/**
 * Turns a scanned code into the row behind it, within the active company (M-02).
 * The client parses the ASL1 grammar (`parseScanPayload`) only to decide what it
 * is looking at; the lookup itself is always the server's. The jsonb is shaped in
 * `stock/scan.ts`, which the scanner also uses for its offline answer, so both
 * paths hand the same object to the result sheet.
 *
 * The cost block is missing for a role without `seesCosts`: the RPC leaves it
 * out, the client does not mask it.
 */
export async function resolveScan(client: AslenClient, companyId: string, code: string): Promise<ScanResolution> {
  return parseScanResolution(unwrap(await client.rpc('resolve_scan', { p_company: companyId, p_code: code })) as Json);
}

export type LocationStockRow = Views<'v_item_stock_by_location'>;

/**
 * The "Ürün · Lot · Son hareket" cells of a scanned location (M-02 §2.4). The
 * RPC answers a location scan with the location row alone, so the counts come
 * from the same per-location balances the item card reads — one narrow query, no
 * cost column, so an operator may run it.
 */
export async function getLocationFacts(client: AslenClient, companyId: string, locationId: string): Promise<ScanLocationFacts> {
  const rows = unwrap(
    await client.from('v_item_stock_by_location').select('item_id, qty_base, lot_count, last_movement_at').eq('company_id', companyId).eq('location_id', locationId),
  );
  return summariseLocationStock(rows);
}

// ── movement ledger and posting (W-07) ──────────────────────────────────────
export type StockLedgerRow = Views<'v_stock_ledger'>;
export type StockReasonCode = Tables<'stock_reason_codes'>;
// the input and the result of the posting engine are shaped in `stock/movement.ts`
// (mobile builds the same payload offline); re-exported so callers of the API do
// not have to import from two places
export type { PostMovementInput, PostedMovement } from '../stock/movement';

/** The reason chips and the ledger's "Sebep" menu; the labels are the company's own (C21). */
export async function listReasonCodes(client: AslenClient, companyId: string): Promise<StockReasonCode[]> {
  return unwrap(await client.from('stock_reason_codes').select('*').eq('company_id', companyId).order('sort').order('code'));
}

export interface LedgerFilters {
  /** Istanbul day bounds from `ledgerRange()` — half-open, so a day is never counted twice. */
  fromTs?: string;
  toTs?: string;
  kinds?: StockMovementKind[];
  /** Matches a movement whose source **or** target sits in one of these warehouses (§4.3). */
  warehouseIds?: string[];
  actorIds?: string[];
  reasonCodeIds?: string[];
  itemId?: string | null;
  /**
   * One lot, by its number — what W-08's "Hareketleri gör" sends. The number and
   * not the id, because the link is also something a partner pastes by hand.
   */
  lotNo?: string | null;
  /**
   * One reference document, by its number: what W-09's approved banner sends
   * ("Hareketleri gör" → every adjustment the count posted).
   */
  refNo?: string | null;
  /** Reversals and reversed rows are hidden until the Tür menu asks for them. */
  includeReversed?: boolean;
}

/** `(occurred_at desc, no desc)` — the keyset the next page continues from. */
export interface LedgerCursor {
  occurredAt: string;
  no: string;
}

export interface LedgerPage {
  rows: StockLedgerRow[];
  /** Absent when this was the last page. */
  next: LedgerCursor | null;
  /** Exact count for the filters, asked for on the first page only (§4.2). */
  total: number | null;
}

export const LEDGER_PAGE_SIZE = 50;
/** Spec §4.2: the CSV covers every matching row up to this cap. */
export const LEDGER_EXPORT_MAX = 10_000;

function ledgerQuery(client: AslenClient, companyId: string, filters: LedgerFilters, opts: { count?: boolean } = {}) {
  let q = client
    .from('v_stock_ledger')
    .select('*', opts.count ? { count: 'exact' } : undefined)
    .eq('company_id', companyId);
  if (filters.fromTs) q = q.gte('occurred_at', filters.fromTs);
  if (filters.toTs) q = q.lt('occurred_at', filters.toTs);
  if (filters.kinds?.length) q = q.in('kind', filters.kinds);
  if (filters.actorIds?.length) q = q.in('created_by', filters.actorIds);
  if (filters.reasonCodeIds?.length) q = q.in('reason_code_id', filters.reasonCodeIds);
  if (filters.itemId) q = q.eq('item_id', filters.itemId);
  if (filters.lotNo) q = q.eq('lot_no', filters.lotNo);
  if (filters.refNo) q = q.eq('ref_no', filters.refNo);
  if (filters.warehouseIds?.length) {
    const ids = filters.warehouseIds.join(',');
    q = q.or(`from_warehouse_id.in.(${ids}),to_warehouse_id.in.(${ids})`);
  }
  if (!filters.includeReversed) q = q.is('reversal_of_id', null).is('reversed_by_id', null);
  return q.order('occurred_at', { ascending: false }).order('no', { ascending: false });
}

/**
 * One page of W-07, newest first. Keyset rather than offset: the ledger grows
 * while it is read (the other partner is posting), and an offset page would show
 * a row twice or skip one. `total` is asked for once, because `count: 'exact'`
 * costs a second scan on every page.
 */
export async function listStockLedger(
  client: AslenClient,
  companyId: string,
  filters: LedgerFilters,
  cursor?: LedgerCursor | null,
  limit = LEDGER_PAGE_SIZE,
): Promise<LedgerPage> {
  let q = ledgerQuery(client, companyId, filters, { count: !cursor });
  if (cursor) {
    // (occurred_at, no) < (cursor) — the tuple comparison PostgREST spells out
    q = q.or(`occurred_at.lt.${cursor.occurredAt},and(occurred_at.eq.${cursor.occurredAt},no.lt.${cursor.no})`);
  }
  const res = await q.limit(limit + 1);
  const rows = unwrap(res);
  const page = rows.slice(0, limit);
  const last = page[page.length - 1];
  return {
    rows: page,
    next: rows.length > limit && last?.occurred_at && last.no ? { occurredAt: last.occurred_at, no: last.no } : null,
    total: cursor ? null : (res.count ?? null),
  };
}

/** Every row behind the active filters, for "Dışa aktar" (capped, §4.2). */
export async function listStockLedgerForExport(client: AslenClient, companyId: string, filters: LedgerFilters): Promise<StockLedgerRow[]> {
  return unwrap(await ledgerQuery(client, companyId, filters).limit(LEDGER_EXPORT_MAX));
}

/** One movement by its document number — what `?m=HRK-2026-01285` opens. */
export async function getMovementByNo(client: AslenClient, companyId: string, no: string): Promise<StockLedgerRow | null> {
  const rows = unwrap(await client.from('v_stock_ledger').select('*').eq('company_id', companyId).eq('no', no).limit(1));
  return rows[0] ?? null;
}

/**
 * The drawer subtitle "HRK-2026-01285 · taslak". Read-only: the number is not
 * consumed, so the row that is finally written may carry a later one if the other
 * partner saves first — which is why the toast quotes the real number.
 */
export async function peekDocumentNo(client: AslenClient, companyId: string, docType = 'stock_movement'): Promise<string | null> {
  return unwrap(await client.rpc('peek_document_no', { p_company: companyId, p_doc_type: docType })) as string | null;
}

/**
 * The only way stock changes. The engine allocates the number, freezes the unit
 * factor, costs the movement and updates the balances in one transaction; a
 * replayed `client_uuid` returns the stored row with `replay: true` instead of a
 * second movement (C33).
 */
export async function postStockMovement(client: AslenClient, input: PostMovementInput): Promise<PostedMovement> {
  return unwrap(await client.rpc('fn_post_stock_movement', { p: movementPayload(input) as Json })) as unknown as PostedMovement;
}

/**
 * "Geri al" inside `settings.stock.undo_seconds`, a partner's reversal after it
 * (then `note` is required). Both post a mirror movement — nothing is deleted,
 * and `already_reversed` counts as success for an undo (C34).
 */
export async function reverseStockMovement(client: AslenClient, id: string, note?: string | null): Promise<PostedMovement> {
  return unwrap(await client.rpc('fn_reverse_stock_movement', { p_id: id, p_note: note ?? undefined })) as unknown as PostedMovement;
}

// ── lots and traceability (W-08) ────────────────────────────────────────────
export type LotOverviewRow = Views<'v_lot_overview'>;
export type LotCostRow = Pick<Tables<'lot_costs'>, 'lot_id' | 'unit_cost_base' | 'unit_cost' | 'unit_cost_currency' | 'is_provisional'>;
// the parsed answers of the two trace RPCs are shaped in `stock/trace.ts`, and
// re-exported here so a caller of the API imports from one place
export type { LotTrace, TraceNodeDetail, TraceDirection } from '../stock/trace';

/** The whole lot cap of W-08a — the same one-query shape W-04 and W-06 use. */
export const LOT_LIST_MAX = 5_000;

/**
 * The rows of the lot browser. `v_lot_overview` has no cost column by design, so
 * the list is readable by every stock role, including the operator who must never
 * see a landed cost.
 *
 * Newest receipt first, which is also the table's default sort; depleted lots stay
 * in the list, because "which lot did that bobbin come from" is asked long after
 * the lot is gone.
 */
export async function listLots(client: AslenClient, companyId: string): Promise<LotOverviewRow[]> {
  return unwrap(
    await client
      .from('v_lot_overview')
      .select('*')
      .eq('company_id', companyId)
      .order('received_at', { ascending: false, nullsFirst: false })
      .order('lot_no', { ascending: false })
      .limit(LOT_LIST_MAX),
  );
}

/** One lot by its number — what `/stock/lots/BOB-26-0138` resolves. */
export async function getLotByNo(client: AslenClient, companyId: string, lotNo: string): Promise<LotOverviewRow | null> {
  const rows = unwrap(await client.from('v_lot_overview').select('*').eq('company_id', companyId).eq('lot_no', lotNo).limit(1));
  return rows[0] ?? null;
}

/**
 * Unit costs for the lots the list shows, for the "Birim maliyet" column.
 * Called only for a `seesCosts` role — and if it ever is not, RLS answers with
 * zero rows rather than a number the role may not see.
 */
export async function listLotCosts(client: AslenClient, companyId: string): Promise<LotCostRow[]> {
  return unwrap(await client.from('lot_costs').select('lot_id, unit_cost_base, unit_cost, unit_cost_currency, is_provisional').eq('company_id', companyId).limit(LOT_LIST_MAX));
}

/**
 * The genealogy. The RPC is `security definer` and re-checks membership, and it
 * leaves the cost fields out for a role without `seesCosts` — the client masks
 * nothing, it simply has nothing to show.
 */
export async function getLotTrace(client: AslenClient, lotId: string, direction: TraceDirection = 'both'): Promise<LotTrace | null> {
  const raw = unwrap(await client.rpc('fn_lot_trace', { p_lot_id: lotId, p_direction: direction })) as Json;
  return parseLotTrace(raw);
}

/** The side panel's payload for the selected node (§4.7). */
export async function getTraceNodeDetail(client: AslenClient, kind: string, refId: string): Promise<TraceNodeDetail | null> {
  const raw = unwrap(await client.rpc('get_trace_node_detail', { p_node_kind: kind, p_ref_id: refId })) as Json;
  return parseTraceNodeDetail(raw);
}

/**
 * Lot maintenance (G3). The RPC is the only way in: it re-checks
 * `private.master_writer`, so an operator's attempt is refused by the database
 * even though the screen already hid the buttons. Quantities are not among the
 * keys it accepts.
 */
export async function updateLot(client: AslenClient, lotId: string, patch: Record<string, Json>): Promise<Json> {
  return unwrap(await client.rpc('update_lot', { p_lot_id: lotId, p_patch: patch as Json })) as Json;
}

// ── count sessions and their review (W-09) ──────────────────────────────────
export type StockCountRow = Views<'v_stock_counts'>;
// the parsed review payload is shaped in `stock/counts.ts` (M-10 reads the same
// numbers), and re-exported here so a caller of the API imports from one place
export type { CountReview, CountReviewLine, CountReviewHeader, CountReviewStats } from '../stock/counts';

/** The list is one query for the whole company, like W-04 and W-06 (§5). */
export const COUNT_LIST_MAX = 500;

/**
 * The rows of /stock/counts: one per session with its scope, its counter, its
 * line counts and — for cost roles only — the value the differences are worth.
 * Newest first, which is also the table's default sort; the chips and the search
 * filter these rows in the browser.
 */
export async function listStockCounts(client: AslenClient, companyId: string, limit = COUNT_LIST_MAX): Promise<StockCountRow[]> {
  return unwrap(
    await client
      .from('v_stock_counts')
      .select('*')
      .eq('company_id', companyId)
      .order('started_at', { ascending: false, nullsFirst: false })
      .order('count_no', { ascending: false })
      .limit(limit),
  );
}

/** One session by its number — what `/stock/counts/SAY-2026-0034` resolves. */
export async function getStockCountByNo(client: AslenClient, companyId: string, countNo: string): Promise<StockCountRow | null> {
  const rows = unwrap(await client.from('v_stock_counts').select('*').eq('company_id', companyId).eq('count_no', countNo).limit(1));
  return rows[0] ?? null;
}

/**
 * The review payload (§6). The RPC is `security definer`: it re-checks
 * membership, stamps `reviewed_at` the first time a partner other than the
 * counter opens it, and leaves the cost fields out for a role without
 * `seesCosts` — the client masks nothing, it simply has nothing to show.
 */
export async function getStockCountReview(client: AslenClient, countId: string): Promise<CountReview | null> {
  const raw = unwrap(await client.rpc('get_stock_count_review', { p_count_id: countId })) as Json;
  return parseCountReview(raw);
}

export interface StartCountInput {
  warehouseId: string;
  /** Empty means every active location of the warehouse (the RPC's own default). */
  locationIds?: string[];
  assignedTo?: string | null;
  /** Defaults to `settings.stock.count_blind_default` server side. */
  isBlind?: boolean;
  note?: string | null;
  kind?: CountKind;
  /** Idempotency: a replayed uuid returns the session that was already started. */
  clientUuid?: string;
}

export interface StartedCount {
  id: string;
  countNo: string;
  isBlind: boolean;
  assignedTo: string | null;
  locationCodes: string[];
  /** True when this call found the session its `clientUuid` had already created. */
  replay: boolean;
}

/**
 * "Sayım başlat" (§5.1). The RPC allocates the number, refuses a location that
 * already has an open session and snapshots the expected quantities per location
 * in one transaction, then answers with the whole session — so the toast can
 * quote the real number instead of a guess.
 */
export async function startStockCount(client: AslenClient, companyId: string, input: StartCountInput): Promise<StartedCount> {
  const raw = unwrap(
    await client.rpc('start_stock_count', {
      p: {
        company_id: companyId,
        warehouse_id: input.warehouseId,
        location_ids: input.locationIds ?? [],
        assigned_to: input.assignedTo ?? null,
        is_blind: input.isBlind ?? null,
        note: input.note ?? null,
        kind: input.kind ?? 'cycle',
        client_uuid: input.clientUuid ?? null,
      } as Json,
    }),
  ) as Record<string, unknown>;
  const count = (raw?.count ?? {}) as Record<string, unknown>;
  const locations = Array.isArray(raw?.locations) ? (raw.locations as Record<string, unknown>[]) : [];
  return {
    id: String(count.id ?? ''),
    countNo: String(count.count_no ?? ''),
    isBlind: count.is_blind === true,
    assignedTo: (count.assigned_to as string | null) ?? null,
    locationCodes: locations.map((l) => String(l.code ?? '')).filter(Boolean),
    replay: raw?.replay === true,
  };
}

export interface ApprovedCount {
  countNo: string;
  batchId: string | null;
  movementIds: string[];
  movementCount: number;
  /** `null` for a role without `seesCosts`. */
  valueImpactBase: string | null;
}

/**
 * The approval (§6.9). One engine posting per selected line, inside one
 * transaction: either every difference is written or none is. Never optimistic
 * (G7) — the movements it creates carry numbers and costs only the server knows,
 * and the negative-stock guard may refuse the whole batch.
 *
 * `reviewVersion` is the optimistic-concurrency check: if the count changed while
 * the confirmation was open the RPC raises `stale_version` instead of posting
 * numbers the reviewer never saw.
 */
export async function approveStockCount(
  client: AslenClient,
  input: { countId: string; lineIds: string[]; reviewVersion?: number | null; note?: string | null },
): Promise<ApprovedCount> {
  const raw = unwrap(
    await client.rpc('approve_stock_count', {
      p_count_id: input.countId,
      p_line_ids: input.lineIds,
      p_review_version: input.reviewVersion ?? undefined,
      p_note: input.note ?? undefined,
    }),
  ) as Record<string, unknown>;
  const ids = Array.isArray(raw?.movement_ids) ? (raw.movement_ids as unknown[]).map(String) : [];
  return {
    countNo: String(raw?.count_no ?? ''),
    batchId: (raw?.batch_id as string | null) ?? null,
    movementIds: ids,
    movementCount: Number(raw?.movement_count ?? ids.length) || 0,
    valueImpactBase: raw?.value_impact_base === null || raw?.value_impact_base === undefined ? null : String(raw.value_impact_base),
  };
}

// ── the phone's half of a count session (M-05) ──────────────────────────────
export type { CountSession, CountLocation, CountLine } from '../stock/session';

/**
 * The whole session in one read: header, locations, lines with their UoM ladders.
 * A blind count leaves `expected_qty` out for anyone who is not owner or partner,
 * so the phone literally cannot show what it must not show. This is what the count
 * store keeps, and what a reconnect merges into (`mergeCountSession`).
 */
export async function getCountSession(client: AslenClient, countId: string): Promise<CountSession | null> {
  return parseCountSession(unwrap(await client.rpc('get_stock_count', { p_count_id: countId })) as Json);
}

/** `start_stock_count` answers with the same payload, so the walk can begin offline right away. */
export async function startCountSession(client: AslenClient, companyId: string, input: StartCountInput): Promise<{ session: CountSession | null; replay: boolean }> {
  const raw = unwrap(
    await client.rpc('start_stock_count', {
      p: {
        company_id: companyId,
        warehouse_id: input.warehouseId,
        location_ids: input.locationIds ?? [],
        assigned_to: input.assignedTo ?? null,
        is_blind: input.isBlind ?? null,
        note: input.note ?? null,
        kind: input.kind ?? 'cycle',
        source: 'mobile',
        client_uuid: input.clientUuid ?? null,
      } as Json,
    }),
  ) as Json;
  return { session: parseCountSession(raw), replay: (raw as { replay?: boolean } | null)?.replay === true };
}

export interface CountLineInput {
  countId: string;
  /** `stock_count_lines.id`, or a client uuid for a line the snapshot did not expect. */
  lineId: string;
  locationId: string;
  itemId: string;
  lotId?: string | null;
  /** As entered, in `unit` — a decimal string. 0 is valid ("bulunamadı"). */
  qtyEntered: string;
  unit: string;
  /** This device's revision: the server ignores one it already has (no-op success). */
  rev: number;
  /** When the operator counted it, which may be hours before the entry is sent. */
  countedAt?: string | null;
  note?: string | null;
}

export interface CountedLineResult {
  lineId: string;
  countedQty: string | null;
  clientRev: number;
  syncedAt: string | null;
  isUnexpected: boolean;
  conflictStatus: string;
  /** True when the server already had this revision (or a newer one). */
  noop: boolean;
}

/**
 * One counted line (§7.7). Idempotent on `id` + `client_rev`: a replay of an older
 * revision after a newer one landed is a no-op success, which is what lets the
 * queue retry freely without ever lowering a count.
 */
export async function upsertCountLine(client: AslenClient, input: CountLineInput): Promise<CountedLineResult> {
  const raw = unwrap(
    await client.rpc('upsert_count_line', {
      p: {
        count_id: input.countId,
        id: input.lineId,
        location_id: input.locationId,
        item_id: input.itemId,
        lot_id: input.lotId ?? null,
        counted_input: input.qtyEntered,
        counted_input_unit: input.unit,
        client_rev: input.rev,
        counted_at: input.countedAt ?? null,
        note: input.note ?? null,
      } as Json,
    }),
  ) as Record<string, unknown>;
  return {
    lineId: String(raw?.id ?? input.lineId),
    countedQty: raw?.counted_qty === null || raw?.counted_qty === undefined ? null : String(raw.counted_qty),
    clientRev: Number(raw?.client_rev ?? input.rev) || 0,
    syncedAt: (raw?.synced_at as string | null) ?? null,
    isUnexpected: raw?.is_unexpected === true,
    conflictStatus: String(raw?.conflict_status ?? 'none'),
    noop: raw?.noop === true,
  };
}

export interface FinishedCountLocation {
  status: string;
  /** Lines a movement touched after the snapshot — the conflict list's input. */
  conflicts: CountConflictRow[];
  noop: boolean;
}

/**
 * "Bu lokasyonu bitir". `markUncountedZero` is the operator's answer to the
 * confirm sheet; without it the RPC refuses a location with uncounted lines
 * (`uncounted_lines`). Finishing a location that is already `done` returns its
 * state, so a replay is safe.
 */
export async function finishCountLocation(
  client: AslenClient,
  input: { countId: string; locationId: string; markUncountedZero?: boolean; clientTs?: string | null },
): Promise<FinishedCountLocation> {
  const raw = unwrap(
    await client.rpc('finish_count_location', {
      p_count_id: input.countId,
      p_location_id: input.locationId,
      p_zero_uncounted: input.markUncountedZero ?? false,
      ...(input.clientTs ? { p_client_ts: input.clientTs } : {}),
    }),
  ) as Record<string, unknown>;
  return { status: String(raw?.status ?? ''), conflicts: parseCountConflicts(raw?.conflicts), noop: raw?.noop === true };
}

/** "Yeniden açıldı" — the undo of a finish, and what the picker does on a `done` row. */
export async function reopenCountLocation(client: AslenClient, input: { countId: string; locationId: string }): Promise<string> {
  const raw = unwrap(await client.rpc('reopen_count_location', { p_count_id: input.countId, p_location_id: input.locationId })) as Record<string, unknown>;
  return String(raw?.status ?? '');
}

/**
 * Online-only: re-snapshots a location nobody has counted yet, so the expected
 * quantities are as of now and most conflicts never happen. It answers with the
 * whole session, which the store then merges.
 */
export async function refreshCountLocationSnapshot(
  client: AslenClient,
  input: { countId: string; locationId: string },
): Promise<{ refreshed: boolean; session: CountSession | null }> {
  const raw = unwrap(await client.rpc('refresh_count_location_snapshot', { p_count_id: input.countId, p_location_id: input.locationId })) as Record<
    string,
    unknown
  >;
  return { refreshed: raw?.refreshed === true, session: raw?.payload ? parseCountSession(raw.payload) : null };
}

/** The two answers a moved line takes: keep the count as it is, or count it again. */
export async function resolveCountConflict(client: AslenClient, input: { lineId: string; action: 'kept' | 'recount' }): Promise<{ conflictStatus: string; noop: boolean }> {
  const raw = unwrap(await client.rpc('resolve_count_conflict', { p: { line_id: input.lineId, action: input.action } as Json })) as Record<string, unknown>;
  return { conflictStatus: String(raw?.conflict_status ?? ''), noop: raw?.noop === true };
}

export interface SubmittedCount {
  status: string;
  countNo: string;
  approvalId: string | null;
  linesCounted: number;
  linesWithVariance: number;
  /**
   * The submit found movements that touched counted lines and **stored** the flags
   * instead of raising (an exception would roll them back): the count stays
   * `in_progress` and these lines are what the operator has to answer.
   */
  countMoved: boolean;
  conflicts: CountConflictRow[];
  noop: boolean;
}

/** "Sayımı gönder" (§3.11). Approval itself is the web's W-09 — this only asks for it. */
export async function submitStockCount(client: AslenClient, input: { countId: string; clientUuid?: string | null }): Promise<SubmittedCount> {
  const raw = unwrap(
    await client.rpc('submit_stock_count', { p_count_id: input.countId, ...(input.clientUuid ? { p_client_uuid: input.clientUuid } : {}) }),
  ) as Record<string, unknown>;
  return {
    status: String(raw?.status ?? ''),
    countNo: String(raw?.count_no ?? ''),
    approvalId: (raw?.approval_id as string | null) ?? null,
    linesCounted: Number(raw?.lines_counted ?? 0) || 0,
    linesWithVariance: Number(raw?.lines_with_variance ?? 0) || 0,
    countMoved: raw?.count_moved === true,
    conflicts: parseCountConflicts(raw?.conflicts),
    noop: raw?.noop === true,
  };
}

/**
 * The 30 s undo of a submit. The server refuses it once a reviewer has opened the
 * count (`count_locked`) or the window has passed (`undo_expired`), which is why
 * the toast's action is the only place that calls it.
 */
export async function withdrawStockCount(client: AslenClient, countId: string): Promise<string> {
  const raw = unwrap(await client.rpc('withdraw_stock_count', { p_count_id: countId })) as Record<string, unknown>;
  return String(raw?.status ?? '');
}

/**
 * "Yeniden saydır" (§6.8): the selected lines lose their counted quantity, the
 * locations reopen and the counter is notified. Nothing is posted, so nothing
 * changes in stock until the recount is submitted and approved.
 */
export async function requestCountRecount(client: AslenClient, input: { countId: string; lineIds: string[]; note?: string | null }): Promise<number> {
  const raw = unwrap(
    await client.rpc('request_recount', { p_count_id: input.countId, p_line_ids: input.lineIds, p_note: input.note ?? undefined }),
  ) as Record<string, unknown>;
  return Number(raw?.lines ?? input.lineIds.length) || 0;
}
